import React, { useRef, useReducer, useState, useEffect } from "react";
import { Wizard, WizardPage, IWizardCommonProps } from "cayo.ui.wizards";
import { useIntl } from "react-intl";
import { ChannelName, Configure } from "../shared";
import { ConfigureServer } from "./steps/ConfigureServer";
import { Summary } from "./steps/Summary";
import { communicationChannelsWizardMessages } from "../shared/messages";
import { smtpWizardMessages } from "./messages";
import wizardStateReducer, { InitialState } from "./store";
import { validateEmails, defineChannelName } from "../shared/helpers";
import { addSmtpConnector } from "../shared/api";
import { SmtpChannelConfig } from "../shared/types";

export const Smtp = (props: IWizardCommonProps) => {
  const intl = useIntl();
  const wizardRef = useRef<{ resetError: () => void }>(null);
  const [isFinished, setIsFinished] = useState<boolean>(false);
  const [loadingText, setLoadingText] = useState<string | undefined>();
  const [wizardState, dispatchWizardState] = useReducer(wizardStateReducer, InitialState);
  const stateRef = useRef(InitialState);
  stateRef.current = wizardState;

  useEffect(() => {
    defineChannelName(
      "cayo.graph.smtpConnector",
      "SMTP Communication Channel",
      setLoadingText,
      dispatchWizardState
    );
  }, []);

  const handleChangeField = (form: string, data: any) => {
    dispatchWizardState({
      type: "update",
      payload: {
        form,
        data,
      },
    });
  };

  return (
    <Wizard
      title={intl.formatMessage(smtpWizardMessages.wizardTitle)}
      isFinished={isFinished}
      activePage={0}
      ref={wizardRef}
      showStepError
      loadingText={loadingText}
      closeConfirmationMessage={" "}
      {...props}
    >
      <WizardPage
        onNext={() => Promise.resolve()}
        title={intl.formatMessage(communicationChannelsWizardMessages.wizardTitle)}
        stepName={intl.formatMessage(communicationChannelsWizardMessages.wizardTitle)}
        subTitle=""
        isInputValid={wizardState?.chanelInfo?.name?.trim()}
      >
        <ChannelName
          name={wizardState?.chanelInfo?.name}
          description={wizardState?.chanelInfo?.description}
          handleChange={(key, value) =>
            handleChangeField("chanelInfo", {
              [key]: value,
            })
          }
        />
      </WizardPage>

      <WizardPage
        onNext={() => Promise.resolve()}
        title={intl.formatMessage(smtpWizardMessages.configureMailServer)}
        stepName={intl.formatMessage(smtpWizardMessages.configureMailServer)}
        subTitle=""
        isInputValid={
          wizardState?.serverConfig?.server?.trim() && wizardState?.serverConfig?.port?.trim()
        }
      >
        <ConfigureServer
          server={wizardState?.serverConfig?.server}
          port={wizardState?.serverConfig?.port}
          tls={wizardState?.serverConfig?.tls}
          user={wizardState?.serverConfig?.user}
          password={wizardState?.serverConfig?.password}
          handleChange={(key, value) => {
            handleChangeField("serverConfig", {
              [key]: value,
            });

            if (key === "user") {
              handleChangeField("senderAndRecipients", {
                sender: value,
              });
            }
          }}
        />
      </WizardPage>

      <WizardPage
        onNext={() => {
          const data: SmtpChannelConfig = {
            name: stateRef.current?.chanelInfo.name,
            description: stateRef.current?.chanelInfo.description,
            accountName: stateRef.current?.serverConfig.user,
            accountPassword: stateRef.current?.serverConfig.password,
            server: stateRef.current?.serverConfig.server,
            port: stateRef.current?.serverConfig.port,
            timeout: stateRef.current?.serverConfig.timeout,
            useSSL: stateRef.current?.serverConfig.tls,
            sender: stateRef.current?.senderAndRecipients.sender,
            recipients: stateRef.current?.senderAndRecipients.recipients,
            sendTestMessage: true,
          };

          return addSmtpConnector(data, () => setIsFinished(true));
        }}
        title={intl.formatMessage(smtpWizardMessages.configSender)}
        stepName={intl.formatMessage(smtpWizardMessages.selectRecipients)}
        subTitle=""
        isInputValid={
          !validateEmails([wizardState?.senderAndRecipients?.sender]) &&
          wizardState?.senderAndRecipients?.recipients?.length &&
          !validateEmails(wizardState?.senderAndRecipients?.recipients)
        }
      >
        <Configure
          sender={wizardState?.senderAndRecipients?.sender}
          recipients={wizardState?.senderAndRecipients?.recipients}
          handleChange={(key, value) =>
            handleChangeField("senderAndRecipients", {
              [key]: value,
            })
          }
        />
      </WizardPage>

      <WizardPage
        onNext={() => Promise.resolve()}
        title={intl.formatMessage(communicationChannelsWizardMessages.summary)}
        stepName={intl.formatMessage(communicationChannelsWizardMessages.summary)}
        subTitle=""
      >
        <Summary
          server={wizardState?.serverConfig.server}
          port={wizardState?.serverConfig.port}
          useTls={wizardState?.serverConfig.tls}
          username={wizardState?.serverConfig.user}
          sender={wizardState?.senderAndRecipients.sender}
          recipients={wizardState?.senderAndRecipients.recipients}
        />
      </WizardPage>
    </Wizard>
  );
};
